import { THERAPIST_DIRECT_REWARDFUL } from "../../config";

/**
 * Rewardful affiliate tracking helpers.
 *
 * Rewardful sets its own first-party cookie, but that cookie only lives in one
 * browser on one device. A prospect can click an affiliate link on their phone
 * and buy weeks later on a laptop, so we mirror the referral into localStorage
 * here and persist it to our own backend on conversion (see useRewardfulConversion).
 */

export interface StoredReferral {
  /** Rewardful's internal referral UUID. */
  referral: string;
  /** Human-readable affiliate token — the value that appeared in ?ref=. */
  refCode?: string;
  /** The URL the visitor landed on, for the referral record. */
  landingUrl: string;
  capturedAt: number;
  /** Payment intent IDs already reported, so a reload cannot double-count. */
  convertedTransactionIds: string[];
}

function safeJsonParse<T>(value: string | null): T | null {
  try {
    return value ? (JSON.parse(value) as T) : null;
  } catch (error) {
    console.error("Failed to parse stored referral:", error);
    return null;
  }
}

function write(referral: StoredReferral): void {
  try {
    localStorage.setItem(THERAPIST_DIRECT_REWARDFUL, JSON.stringify(referral));
  } catch (error) {
    console.error("Failed to persist referral:", error);
  }
}

export function getStoredReferral(): StoredReferral | null {
  return safeJsonParse<StoredReferral>(
    localStorage.getItem(THERAPIST_DIRECT_REWARDFUL)
  );
}

/**
 * Runs `cb` once Rewardful has parsed the URL. No-ops if the snippet never
 * loaded (ad blocker, offline) — tracking is best-effort and must never throw
 * into the app.
 */
export function onRewardfulReady(cb: () => void): void {
  try {
    if (typeof window.rewardful !== "function") return;
    window.rewardful("ready", cb);
  } catch (error) {
    console.error("Rewardful ready handler failed:", error);
  }
}

/**
 * Mirrors the current Rewardful referral into localStorage.
 *
 * Deliberately only ever writes — never clears. Every purchase ends in a full
 * page reload onto a URL with no ?ref= (confirmPayment uses redirect: 'always'),
 * so clearing when no referral is present would wipe the attribution moments
 * before the conversion is reported.
 */
export function captureReferral(): void {
  onRewardfulReady(() => {
    const referral = window.Rewardful?.referral;
    if (!referral) return;

    const existing = getStoredReferral();
    if (existing?.referral === referral) return; // already captured this visit

    write({
      referral,
      refCode: window.Rewardful?.affiliate?.token,
      landingUrl: window.location.href,
      capturedAt: Math.floor(Date.now() / 1000),
      // A new affiliate link starts a fresh attribution, so conversion history
      // from the previous referral does not carry over.
      convertedTransactionIds: [],
    });
  });
}

export function hasTransactionConverted(transactionId: string): boolean {
  const stored = getStoredReferral();
  return Boolean(stored?.convertedTransactionIds?.includes(transactionId));
}

export function markTransactionConverted(transactionId: string): void {
  const stored = getStoredReferral();
  if (!stored) return;
  if (stored.convertedTransactionIds?.includes(transactionId)) return;

  write({
    ...stored,
    convertedTransactionIds: [
      ...(stored.convertedTransactionIds ?? []),
      transactionId,
    ],
  });
}

/**
 * Reports the conversion to Rewardful. Rewardful matches it to the Stripe
 * customer by email, so this must be the same address the Stripe customer
 * carries or the conversion will never link to the subscription.
 */
export function trackConversion(email: string): void {
  try {
    if (typeof window.rewardful !== "function") return;
    // The inline shim queues this if r.wdfl.co has not finished loading yet.
    window.rewardful("convert", { email });
  } catch (error) {
    console.error("Rewardful convert failed:", error);
  }
}
